import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, filter, fromEvent, Subject, take, takeUntil } from 'rxjs';
import { Dialog } from '@angular/cdk/dialog';
import dayjs from 'dayjs';
import relativeTime from "dayjs/plugin/relativeTime"; 
dayjs.extend(relativeTime)

import { ChatService } from '../chat.service';
import { HomeService, State } from '../../home/home.service';
import { LayoutService } from '../../layout.service';
import { AuthService } from '../../auth.service';
import { MatchesService } from '../../my-matches/matches.service';
import { UrlService } from '../../url.service';
import { WebsocketService } from '../websocket.service';
import { StoriesService } from '../../stories.service';

import { ConversationDialogComponent } from '../../dialog/conversation-dialog/conversation-dialog.component';
import { UnmatchDialogComponent } from '../../dialog/unmatch-dialog/unmatch-dialog.component';
import { BlockUserDialogComponent } from '../../dialog/block-user-dialog/block-user-dialog.component';
import { ReportUserDialogComponent } from '../../dialog/report-user-dialog/report-user-dialog.component';
import { DeleteConversationDialogComponent } from '../../dialog/delete-conversation-dialog/delete-conversation-dialog.component';
import { GoBackButtonComponent } from '../../shared/go-back-button/go-back-button.component';

import { User } from '../../shared/interfaces/user';
import { UserTiny } from '../../shared/interfaces/user-tiny';
import { Talk } from '../../shared/interfaces/talk';

@Component({
  selector: 'app-user-tiny',
  standalone: true,
  imports: [RouterModule, GoBackButtonComponent, CommonModule],
  templateUrl: './user-tiny.component.html',
  styleUrl: './user-tiny.component.scss'
})
export class UserTinyComponent implements OnInit, OnDestroy {
  @Input() data!: UserTiny | null | undefined
  @Input() new!: boolean
  @Input() newMessages!: number
  @Input() origin!: string
  @Input() link!: string
  @Input() talk!: Talk
  @Output() selectedTalkId: EventEmitter<number> = new EventEmitter
  
  user = new BehaviorSubject<User | null>(null)
  dialog = inject(Dialog)
  loggedIn = false
  matchProfile!: User | undefined
  private readonly _destroyed = new Subject<void>()
  isDesktop = this.layoutService.isDesktop
  dateExpression = ''
  timeExpression = ''
  
  constructor(
    private chatService: ChatService,
    private homeService: HomeService,
    private router: Router,
    private layoutService: LayoutService,
    private authService: AuthService,
    private matchesService: MatchesService,
    private urlService: UrlService,
    private ws: WebsocketService,
    private storiesService: StoriesService,
    private route: ActivatedRoute
  ){}

  ngOnInit(){
    console.log('ngoninit user tiny')
    if(this.talk) this.talk.last_message.content = decodeURIComponent(this.talk.last_message.content)

    if(this.data){  
      this.authService.getProfile(this.data.user.id).pipe(takeUntil(this._destroyed)).subscribe(user => {
        if(user.connected_at){
          const profileLastConnectionDate = dayjs(user.connected_at)
          const today = dayjs(new Date())
          const timeDiff = today.diff(profileLastConnectionDate, 'day')
          this.loggedIn = timeDiff <= 1
        } else {
          const match = this.matchesService.matches.filter(m => m.id === this.data?.user.id)
          if(!match[0]) return
          const profileLastConnectionDate = dayjs(match[0].connected_at)
          const today = dayjs(new Date())
          const timeDiff = today.diff(profileLastConnectionDate, 'day')
          this.loggedIn = timeDiff <= 1
        }
      })
    } else {
      this.chatService.relatedUser.subscribe(res => {
        this.data = res
  
        if(!res) return
        this.authService.getProfile(res.user.id).pipe(takeUntil(this._destroyed)).subscribe(user => {
          if(this.chatService.messageSent.value) return
          if(user.connected_at){
            const profileLastConnectionDate = dayjs(user.connected_at)
            const today = dayjs(new Date())
            const timeDiff = today.diff(profileLastConnectionDate, 'day')
            this.loggedIn = timeDiff <= 1
          } else {
            const match = this.matchesService.matches.filter(m => m.id === res.user.id)
            if(!match[0]) return
            if(match[0].connected_at){
              const profileLastConnectionDate = dayjs(match[0].connected_at)
              const today = dayjs(new Date())
              const timeDiff = today.diff(profileLastConnectionDate, 'day')
              this.loggedIn = timeDiff <= 1
            }
          }
        })
      })
    }

    if(!this.data) return
    const time = dayjs().to(dayjs(this.data.time))
    let dateStr = ''
    let timeStr = ''

    if(time.includes('hour') || time.includes('minute') || time.includes('second')){
      dateStr = 'Today'
    } else if(time === 'a day ago') {
      dateStr = 'Yesterday'
    } else {
      dateStr = dayjs(this.data.time).format('YYYY-MM-DD')
    }

    timeStr = dayjs(this.data.time).format('hh:mm A')

    if(dateStr === timeStr && dateStr !== 'Invalid Date'){
      this.dateExpression = dateStr
      this.timeExpression = ''
    } else if(dateStr === timeStr && dateStr === 'Invalid Date'){
      this.dateExpression = 'Now'
      this.timeExpression = ''
    } else {
      this.dateExpression = dateStr !== 'Invalid Date' ? dateStr : 'Now'
      this.timeExpression = timeStr !== 'Invalid Date' ? timeStr : ''  
    }

    // enables to close the conversation opened using the browser arrows
    if(!this.isDesktop()){
      fromEvent(window, 'popstate').pipe(take(1)).subscribe(()=>{
        this.router.navigate(['/home/my-matches/chat'])

        if(this.urlService.getPreviousUrl()?.includes('/new-encounters')){
          this.homeService.pageDisplayed.next('new-encounters')
        }

        this.chatService.toggleSidenav.next(false)
      })
    }
  }

  checkIfTodayOrYesterday(time: number) {
    if(!this.data) return
    const today = new Date()
    const date = new Date(time).toDateString()
    const now = today.toDateString()
    const yesterday = new Date(today.setDate(today.getDate() - 1)).toDateString()
  
    this.data.today = date === now
    this.data.yesterday = date === yesterday
  }

  openTalk(data?: UserTiny | null){
    if(this.origin === 'conversation') return

    if(data) this.chatService.relatedUser.next(data)

    if(data && data.talking) {
      this.ws.connect(data.talking)
      this.chatService.setConversationToShow(data.talking)
    }
    
    if(!this.isDesktop()) {
      this.toggleSidenav(true, 'conversation')
      this.homeService.pageDisplayed.next('mobile-conversation')
    }
    
    this.router.navigate([this.link])
  }

  toggleSidenav(open: boolean, componentRequested: string){
    this.chatService.toggleSidenav.next(open)
    this.chatService.componentDisplayed.next(componentRequested)
  }

  openConversationDialog(e: Event) {
    e.stopPropagation()
    this.dialog.open(ConversationDialogComponent, {
      minWidth: '300px',
      maxWidth: '450px',
      width: '100vw',
    }).closed.pipe(
      filter(res => res !== ''),
    ).subscribe((res) => {
      switch (res) {
        case 'unmatch':
          this.unmatchDialog()
          break;
        case 'block':
          this.blockUserDialog()
          break;
        case 'report':
          this.blockAndReportUserDialog()
          break;
        case 'delete': 
          this.deleteConversationDialog()
          break;
        default:
          break;
      }
    })
  }

  unmatchDialog() {
    this.dialog.open(UnmatchDialogComponent, {
      minWidth: '300px',
      maxWidth: '450px',
      width: '100vw',
    }).closed.pipe(
      filter(res => !!res)
    ).subscribe(() => {
      if(!this.user.value?.id && !this.data?.user?.id) return

      let talkId!: number
      if(!this.route.snapshot.url[this.route.snapshot.url.length - 1].path.includes('new'))  talkId = parseInt(this.route.snapshot.url[this.route.snapshot.url.length - 1].path)
      else talkId = this.chatService.talkingId.value || 0

      if(this.user.value){
        this.homeService.deleteRelationship(this.user.value?.id).pipe(takeUntil(this._destroyed)).subscribe(() => {
          this.chatService.messageSent.next(null)
          this.chatService.relationshipDeleted.next(true)

          const index = this.matchesService.matches.findIndex(m => m.id === this.user.value?.id)
          if(index > -1) this.matchesService.matches.splice(index, 1)
          this.closeConversation(this.data?.talking || talkId)
        })
      } else {
        this.homeService.deleteRelationship(this.data!.user.id).pipe(takeUntil(this._destroyed)).subscribe(() => {
          this.chatService.messageSent.next(null)
          this.chatService.relationshipDeleted.next(true)

          const index = this.matchesService.matches.findIndex(m => m.id === this.data!.user.id)
          if(index > -1) this.matchesService.matches.splice(index, 1)
          this.closeConversation(this.data?.talking || talkId)
        })
      }
    })
  }

  blockUserDialog() {
    this.dialog.open(BlockUserDialogComponent, {
      minWidth: '300px',
      maxWidth: '450px',
      width: '100vw',
    }).closed.pipe(
      filter(res => !!res)
    ).subscribe(() => {
      if(!this.user.value?.id && !this.data?.user?.id) return

      let talkId!: number
      if(!this.route.snapshot.url[this.route.snapshot.url.length - 1].path.includes('new'))  talkId = parseInt(this.route.snapshot.url[this.route.snapshot.url.length - 1].path)
      else talkId = this.chatService.talkingId.value || 0

      if(this.user.value){
        this.homeService.updateRelationship(this.user.value.id, State.blocked).pipe(takeUntil(this._destroyed)).subscribe(() => {
          this.chatService.messageSent.next(null)
          this.matchesService.rejectedProfile.next(this.user.value!.id)
          this.storiesService.blockedUserId = this.user.value!.id

          const index = this.matchesService.matches.findIndex(m => m.id === this.user.value!.id)
          if(index > -1) this.matchesService.matches.splice(index, 1)

          this.closeConversation(this.data?.talking || talkId)
        })
      } else {
        this.homeService.updateRelationship(this.data!.user.id, State.blocked).pipe(takeUntil(this._destroyed)).subscribe(() => {
          this.chatService.messageSent.next(null)
          this.matchesService.rejectedProfile.next(this.data!.user.id)
          this.storiesService.blockedUserId = this.data!.user.id

          const index = this.matchesService.matches.findIndex(m => m.id === this.data!.user.id)
          if(index > -1) this.matchesService.matches.splice(index, 1)

          this.closeConversation(this.data?.talking || talkId)
        })
      }
    })
  }

  blockAndReportUserDialog() {
    this.dialog.open(ReportUserDialogComponent, {
      minWidth: '300px',
      maxWidth: '450px',
      width: '100vw',
    }).closed.pipe(
      filter(res => !!res)
    ).subscribe(() => {
      if(!this.user.value?.id && !this.data?.user?.id) return

      let talkId!: number
      if(!this.route.snapshot.url[this.route.snapshot.url.length - 1].path.includes('new'))  talkId = parseInt(this.route.snapshot.url[this.route.snapshot.url.length - 1].path)
      else talkId = this.chatService.talkingId.value || 0

      if(this.user.value){
        this.homeService.updateRelationship(this.user.value!.id, State.blockedWithReport).pipe(takeUntil(this._destroyed)).subscribe(() => {
          this.chatService.messageSent.next(null)
          this.matchesService.rejectedProfile.next(this.user.value!.id)
          this.storiesService.blockedUserId = this.user.value!.id

          const index = this.matchesService.matches.findIndex(m => m.id === this.user.value!.id)
          if(index > -1) this.matchesService.matches.splice(index, 1)

          this.closeConversation(this.data?.talking || talkId)
        })
      } else {
        this.homeService.updateRelationship(this.data!.user.id, State.blockedWithReport).pipe(takeUntil(this._destroyed)).subscribe(() => {
          this.chatService.messageSent.next(null)
          this.matchesService.rejectedProfile.next(this.data!.user.id)
          this.storiesService.blockedUserId = this.data!.user.id

          const index = this.matchesService.matches.findIndex(m => m.id === this.data!.user.id)
          if(index > -1) this.matchesService.matches.splice(index, 1)

          this.closeConversation(this.data?.talking || talkId)
        })
      }
    }) 
  }

  deleteConversationDialog() {
    this.dialog.open(DeleteConversationDialogComponent, {
      minWidth: '300px',
      maxWidth: '450px',
      width: '100vw',
    }).closed.pipe(
      filter(res => !!res)
    ).subscribe(() => {
      let talkId!: number
      if(!this.route.snapshot.url[this.route.snapshot.url.length - 1].path.includes('new'))  talkId = parseInt(this.route.snapshot.url[this.route.snapshot.url.length - 1].path)
      else talkId = this.chatService.talkingId.value || 0

      this.chatService.deleteTalking(this.data?.talking || talkId).pipe(takeUntil(this._destroyed)).subscribe(() => {
        this.chatService.messageSent.next(null)
        if(this.user.value){
          this.matchesService.rejectedProfile.next(this.user.value!.id)
          this.storiesService.blockedUserId = this.user.value!.id

          const index = this.matchesService.matches.findIndex(m => m.id === this.user.value!.id)
          if(index > -1) this.matchesService.matches.splice(index, 1)
        } else {
          this.matchesService.rejectedProfile.next(this.data!.user.id)
          this.storiesService.blockedUserId = this.data!.user.id

          const index = this.matchesService.matches.findIndex(m => m.id === this.data!.user.id)
          if(index > -1) this.matchesService.matches.splice(index, 1)
        }
        

        this.closeConversation(this.data?.talking || talkId)
      })
    })
  }

  closeConversation(talkId: number){
    this.chatService.setConversationToShow(null)
    this.chatService.conversationClosed.next(talkId.toString())

    if(!this.isDesktop()) {
      this.chatService.toggleSidenav.next(false)
      this.homeService.pageDisplayed.next('my-matches')
    }
  }

  viewProfile(relatedUserId: number | undefined){
    if(!relatedUserId) return
    this.router.navigate([`/home/my-matches/profile/${relatedUserId}/space`])
    this.chatService.origin = this.origin
  }

  goBack(){
//     if(this.isDesktop()){
// const previousUrl = this.urlService.getPreviousUrl()

//     if(previousUrl?.includes('/new-encounters')){
//       this.homeService.pageDisplayed.next('new-encounters')
//     }

//     this.location.back()
//     this.chatService.toggleSidenav.next(false)
//     }
    
    this.chatService.toggleSidenav.next(false)
     this.homeService.pageDisplayed.next('my-matches')
  }

  ngOnDestroy(){
    this._destroyed.next();
    this._destroyed.complete();    
  }
}
